import {BehaviorSubject, Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {Simulation} from "../models/Simulation";
import {Expiries} from "../../services/expiry/Expiries";
import {HistoricalPriceRange} from "../../services/price/historical/HistoricalPriceModels";
import {TradeSimulatorViewServiceUtil} from "./TradeSimulatorViewServiceUtil";
import {SimulationDates} from "../models/SimulationDates";
import {LocalStorageService} from "../../types/NgStorage";
import {TradeSimulationService} from "./TradeSimulationService";
import {Optional} from "../../js/util/Optional";
import {SimulationCalculationResult} from "../models/SimulationCalculationResult";
import {SimulationTrade} from "../models/SimulationTrade";
import {NewSimulationRecord} from "../models/TradeSimulationRecords";
import {isNotNull, Pair} from "../../js/util/UtilFunctions";
import {HistoricalPriceService} from "../../services/price/historical/HistoricalPriceService";
import {IsoDate} from "../../pseudo-types/DateTypes";

const LOCAL_STORAGE_KEY = "tfSimulationViewSelectedSimulationId";

export class TradeSimulatorViewStore {

    private readonly selectedDateCache = new Map<number, IsoDate>();

    private readonly _simulations$ = new ReplaySubject<Array<Simulation>>(1);
    private readonly _currentId$ = new ReplaySubject<number | null>(1);
    private readonly _currentDate$ = new BehaviorSubject<IsoDate | null>(null);
    private readonly _update$ = new Subject<Simulation>();

    private readonly _currentSimulation$: Observable<Simulation | null>;
    private readonly _currentTrades: Observable<Array<SimulationTrade>>;
    private readonly _currentResults$: Observable<SimulationCalculationResult | null>;
    private readonly _expiries$: Observable<Expiries>;
    private readonly _holidays$: Observable<Array<Date>>;
    private readonly _priceRange$: Observable<HistoricalPriceRange>;
    private readonly _simulationExpiries$: Observable<Array<IsoDate>>;
    private readonly _dateRange$: Observable<SimulationDates>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    private constructor(private $localStorage: LocalStorageService,
                        private simulationService: TradeSimulationService,
                        private historicalPriceService: HistoricalPriceService,
                        expiries: Expiries,
                        holidays: Array<Date>) {
        this._expiries$ = Observable.of(expiries);
        this._holidays$ = Observable.of(holidays);

        this._currentSimulation$ = this.initCurrentSimulation();
        this._currentTrades = this.initCurrentTrades();
        this._currentResults$ = this.initCurrentResults();
        this._simulationExpiries$ = this.initAvailableExpiries();
        this._priceRange$ = this.initPriceRange();
        this._dateRange$ = this.initDateRange();

        this.initCurrentSelection();
        this.listSimulations();
        this.watchUpdates();
        this.watchForNewStartingDate();
        this.saveCurrentSelectedDate();
        this.watchCurrentDate();
    }

    private initCurrentSimulation(): Observable<Simulation | null> {
        const idChange$ = this._currentId$;

        return Observable.combineLatest(idChange$, this.simulations)
                         .takeUntil(this.destroyed$)
                         .map(([id, simulations]) => {
                             return Optional.of(id)
                                            .map(i => simulations.find((s) => s.id === i))
                                            .orNull();
                         })
                         .do((simulation) => console.debug(`Current Simulation: `, simulation))
                         .observeOn(Scheduler.async)
                         .shareReplay(1);
    }

    private initCurrentTrades(): Observable<Array<SimulationTrade>> {
        return this._currentSimulation$
                   .takeUntil(this.destroyed$)
                   .map((sim) => {
                       if (sim == null) {
                           return [];
                       } else {
                           return sim.trades;
                       }
                   })
                   .do((trades) => console.debug(`Current Trades: `, trades))
                   .observeOn(Scheduler.async)
                   .shareReplay(1);
    }

    private initCurrentResults(): Observable<SimulationCalculationResult | null> {
        return this.currentSimulation
                   .takeUntil(this.destroyed$)
                   .switchMap(async (sim) => {
                       if (sim === null) {
                           return null;
                       } else {
                           const result = await this.simulationService.calculate(sim);
                           return SimulationCalculationResult.create(sim.id, result);
                       }
                   })
                   .do((results) => console.debug(`Current Results: `, results))
                   .observeOn(Scheduler.async)
                   .publish()
                   .refCount();
    }

    private initAvailableExpiries(): Observable<Array<IsoDate>> {
        return Observable.combineLatest(this.currentSimulation, this.currentResults, this.expiries)
                         .takeUntil(this.destroyed$)
                         .do((args) => console.debug(`Calculating Simulation Expiries: `, ...args))
                         .map(([simulation, results, expiries]) => {
                             if (simulation == null || results == null || expiries == null) {
                                 return [];
                             } else {
                                 return TradeSimulatorViewServiceUtil.availableExpiries(simulation, results, expiries);
                             }
                         })
                         .do((expiries) => console.debug(`Available Expiries: `, expiries))
                         .observeOn(Scheduler.async)
                         .shareReplay(1);
    }

    private initDateRange(): Observable<SimulationDates> {
        return Observable.combineLatest(this.priceRange, this.holidays)
                         .takeUntil(this.destroyed$)
                         .map(([range, holidays]) => {
                             return TradeSimulatorViewServiceUtil.calculateDateRange(range, holidays);
                         })
                         .do((dates) => console.debug(`Current Date Range: `, dates.from, dates.to))
                         .observeOn(Scheduler.async)
                         .shareReplay(1);
    }

    private initCurrentSelection() {
        const selected = this.$localStorage[LOCAL_STORAGE_KEY];
        this.select(selected);
    }

    private initPriceRange() {
        return this.currentSimulation
                   .takeUntil(this.destroyed$)
                   .filter(isNotNull)
                   .map((sim) => sim.underlying)
                   .map((code) => this.historicalPriceService.priceRange(code))
                   .switchMap((resp) => Observable.fromPromise(resp))
                   .observeOn(Scheduler.async)
                   .shareReplay(1);
    }

    private watchUpdates() {
        this._update$
            .takeUntil(this.destroyed$)
            .do((simulation) => console.debug(`Updating Simulation: `, simulation))
            .map((simulation) => this.simulationService.update(simulation))
            .switchMap((p) => Observable.fromPromise(p).catch(() => Observable.of(1)))
            .do(() => console.debug(`Simulation Updated`))
            .subscribeOn(Scheduler.async)
            .subscribe(() => this.listSimulations());
    }

    private watchForNewStartingDate() {
        this.currentSimulation
            .distinctUntilChanged(TradeSimulatorViewServiceUtil.distinctSimulations())
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .subscribeOn(Scheduler.async)
            .subscribe((simulation) => {
                const startingDate = Optional.of(this.selectedDateCache)
                                             .map((cache) => cache.get(simulation.id))
                                             .orElseGet(() => simulation.activeDate);
                console.debug(`Simulation changed, selecting new starting date:`, startingDate);
                this.updateCurrentDate(startingDate);
            });
    }

    /* This is a complete side effect that should have no impact on the rest of the flow of the system */
    private saveCurrentSelectedDate() {

        const currentDate = (simulation: Simulation | null) => {
            return this._currentDate$.skip(1).map((date) => Pair(date, simulation));
        };

        this.currentSimulation
            .takeUntil(this.destroyed$)
            .switchMap(currentDate)
            .do(([date, simulation]) => console.debug("Saving Date", date, "for simulation", simulation))
            .subscribe(([date, simulation]) => {
                if (date == null || simulation == null) {
                    return;
                } else if (date !== simulation.activeDate) {
                    const copy = simulation.copy({activeDate: date});
                    this.simulationService.update(copy);
                    this.selectedDateCache.set(copy.id, date);
                }
            });
    }

    private watchCurrentDate() {
        this.currentDate.subscribe((date) => console.debug("Current Selected Date:", date));
    }

    private async listSimulations() {
        const records = await this.simulationService.list();
        const simulations = records.map(r => Simulation.create(r));
        this.selectedDateCache.clear();
        this._simulations$.next(simulations);
    }

    public get simulations(): Observable<Array<Simulation>> {
        return this._simulations$;
    }

    public get currentSimulation(): Observable<Simulation | null> {
        return this._currentSimulation$;
    }

    public get trades(): Observable<Array<SimulationTrade>> {
        return this._currentTrades;
    }

    public get currentResults(): Observable<SimulationCalculationResult | null> {
        return this.results.shareReplay(1);
    }

    public get results(): Observable<SimulationCalculationResult | null> {
        return this._currentResults$;
    }

    public get currentDate(): Observable<IsoDate | null> {
        return this._currentDate$.asObservable();
    }

    public updateCurrentDate(date: IsoDate) {
        this._currentDate$.next(date);
    }

    public get expiries(): Observable<Expiries> {
        return this._expiries$;
    }

    public get holidays(): Observable<Array<Date>> {
        return this._holidays$;
    }

    public get priceRange(): Observable<HistoricalPriceRange> {
        return this._priceRange$;
    }

    public get simulationExpiries(): Observable<Array<IsoDate>> {
        return this._simulationExpiries$;
    }

    public get dateRange(): Observable<SimulationDates> {
        return this._dateRange$;
    }

    public select(id: number | null) {
        const previous = this.$localStorage[LOCAL_STORAGE_KEY];
        console.debug("Setting Simulation Selection to", id, "from", previous);
        this.$localStorage[LOCAL_STORAGE_KEY] = id;
        this._currentId$.next(id);
    }

    public create(simulation: NewSimulationRecord) {
        this._create(simulation);
    }

    private async _create(simulation: NewSimulationRecord) {
        const newSimulationId = await this.simulationService.create(simulation);
        this.select(newSimulationId);
        await this.listSimulations();
    }

    public update(simulation: Simulation) {
        this._update$.next(simulation);
    }

    public delete(id: number) {
        this._delete(id);
    }

    public async deleteAll() {
        await this.simulationService.deleteAll();
        this.listSimulations();
    }

    private async _delete(id: number) {
        await this.simulationService.delete(id);
        this.listSimulations();
    }

    public clearSimulation() {
        this.currentSimulation
            .take(1)
            .timeout(0)
            .filter(isNotNull)
            .map(s => s.clear())
            .flatMap(s => this.simulationService.update(s))
            .subscribe(s => this.listSimulations());
    }

    public stop() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    public static initialize(expiries: Expiries,
                             holidays: Array<Date>,
                             $localStorage: LocalStorageService,
                             service: TradeSimulationService,
                             historicalPriceService: HistoricalPriceService): TradeSimulatorViewStore {
        return new TradeSimulatorViewStore($localStorage,
                                           service,
                                           historicalPriceService,
                                           expiries,
                                           holidays);
    }
}
